import React, { useState, useEffect, useRef } from 'react';
import { X, Map, List, LayoutDashboard, CalendarCheck, Sparkles, User, Settings, Network } from 'lucide-react';
import SimpleFilters from './SimpleFilters';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

const NAV_ITEMS = [
  { id: 'map',       label: 'Map',       icon: Map },
  { id: 'visits',    label: 'Visits',    icon: CalendarCheck },
  { id: 'list',      label: 'List',      icon: List },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'graph',     label: 'Graph',     icon: Network },
  { id: 'briefing',  label: 'AI Brief',  icon: Sparkles },
  { id: 'profile',   label: 'Profile',   icon: User },
  { id: 'settings',  label: 'Settings',  icon: Settings },
];

const MobileNav = ({
  isOpen,
  onClose,
  currentView,
  onViewChange,
  onGetBriefing,
  currentUser,
  visitCount = 0,
  isBriefingActive = false,
  // filter props
  onSimpleFilterChange,
  currentSimpleFilters,
  isH3Visible,
  h3ActiveView,
  onH3ViewChange,
  h3RevenueFilterRange,
  maxRevenueValue,
  onH3RevenueRangeChange,
  h3SalesFilterRange,
  h3TrafficFilterRange,
  maxSalesValue,
  maxTrafficValue,
  onH3SalesRangeChange,
  onH3TrafficRangeChange,
  onResetH3Filters,
  // nav props
  onShowProfile,
  onShowSettings,
  onShowMySettings,
  onShowTeamSettings,
}) => {
  const [activeSection, setActiveSection] = useState('map');
  const sheetRef = useRef(null);
  const dragStartY = useRef(null);
  const dragCurrentY = useRef(null);

  // Reset active section when sheet opens
  useEffect(() => {
    if (isOpen) setActiveSection(currentView || 'map');
  }, [isOpen, currentView]);

  // Swipe-down to dismiss
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;

    const onStart = (e) => {
      dragStartY.current = e.touches[0].clientY;
    };
    const onMove = (e) => {
      dragCurrentY.current = e.touches[0].clientY;
    };
    const onEnd = () => {
      if (dragStartY.current !== null && dragCurrentY.current !== null) {
        const dy = dragCurrentY.current - dragStartY.current;
        if (dy > 80) onClose();
      }
      dragStartY.current = null;
      dragCurrentY.current = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [onClose]);

  const handleNavTap = (id) => {
    setActiveSection(id);
    if (id === 'briefing') {
      onGetBriefing?.();
      onClose();
      return;
    }
    if (id === 'profile') {
      onShowProfile?.();
      onClose();
      return;
    }
    if (id === 'settings') {
      if (currentUser?.role === 'admin') {
        onShowSettings?.();
      } else {
        onShowMySettings?.();
      }
      onClose();
      return;
    }
    // For map/list/visits/dashboard/graph: navigate + close
    if (['map', 'list', 'visits', 'dashboard', 'graph'].includes(id)) {
      onViewChange(id);
      onClose();
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'map':
      case 'list':
        return (
          <div className="p-4 space-y-4">
            {onSimpleFilterChange && currentSimpleFilters && (
              <SimpleFilters
                onFilterChange={onSimpleFilterChange}
                currentFilters={currentSimpleFilters}
              />
            )}
            {activeSection === 'map' && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Map Layers</div>
                {[
                  { key: 'sales', label: 'Sales Data' },
                  { key: 'traffic', label: 'Traffic Density' },
                  { key: 'revenue', label: 'Revenue (Transactions)' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={h3ActiveView === key && isH3Visible}
                      onChange={() => onH3ViewChange?.(key)}
                      className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                    />
                    <span className="text-sm text-white">{label}</span>
                  </label>
                ))}
                {isH3Visible && h3SalesFilterRange && h3TrafficFilterRange && (
                  <div className="p-3 bg-gray-800 rounded-lg space-y-4 mt-2">
                    {h3ActiveView === 'sales' && (
                      <div>
                        <div className="text-xs font-semibold text-gray-400 mb-3">
                          SALES: ${h3SalesFilterRange[0].toLocaleString()} – ${h3SalesFilterRange[1].toLocaleString()}
                        </div>
                        <Slider
                          range min={0} max={maxSalesValue} value={h3SalesFilterRange}
                          onChange={onH3SalesRangeChange}
                          trackStyle={[{ backgroundColor: '#10b981' }]}
                          handleStyle={[{ borderColor: '#10b981', backgroundColor: '#10b981' }, { borderColor: '#10b981', backgroundColor: '#10b981' }]}
                          railStyle={{ backgroundColor: '#374151' }}
                        />
                      </div>
                    )}
                    {h3ActiveView === 'traffic' && (
                      <div>
                        <div className="text-xs font-semibold text-gray-400 mb-3">
                          TRAFFIC: {h3TrafficFilterRange[0]} – {h3TrafficFilterRange[1]}
                        </div>
                        <Slider
                          range min={0} max={maxTrafficValue} value={h3TrafficFilterRange}
                          onChange={onH3TrafficRangeChange}
                          trackStyle={[{ backgroundColor: '#10b981' }]}
                          handleStyle={[{ borderColor: '#10b981', backgroundColor: '#10b981' }, { borderColor: '#10b981', backgroundColor: '#10b981' }]}
                          railStyle={{ backgroundColor: '#374151' }}
                        />
                      </div>
                    )}
                    {h3ActiveView === 'revenue' && h3RevenueFilterRange && (
                      <div>
                        <div className="text-xs font-semibold text-gray-400 mb-3">
                          REVENUE: ¥{h3RevenueFilterRange[0].toLocaleString()} – ¥{h3RevenueFilterRange[1].toLocaleString()}
                        </div>
                        <Slider
                          range min={0} max={maxRevenueValue} value={h3RevenueFilterRange}
                          onChange={onH3RevenueRangeChange}
                          trackStyle={[{ backgroundColor: '#f59e0b' }]}
                          handleStyle={[{ borderColor: '#f59e0b', backgroundColor: '#f59e0b' }, { borderColor: '#f59e0b', backgroundColor: '#f59e0b' }]}
                          railStyle={{ backgroundColor: '#374151' }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { onResetH3Filters?.(); onClose(); }}
                  className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold text-white text-sm transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[54]"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="fixed inset-x-0 bottom-0 z-[55] bg-gray-900 rounded-t-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '92vh' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-600" />
        </div>

        {/* Header */}
        <div className="px-4 pb-3 flex items-center justify-between flex-shrink-0 border-b border-gray-800">
          <span className="text-white font-bold text-lg">Menu</span>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Nav pills */}
        <div className="flex gap-2 px-4 py-3 overflow-x-auto flex-shrink-0 border-b border-gray-800">
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
            const isActive = activeSection === id;
            const showBadge = id === 'visits' && visitCount > 0;
            return (
              <button
                key={id}
                onClick={() => handleNavTap(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all flex-shrink-0 relative ${
                  isActive
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <Icon size={14} />
                {label}
                {showBadge && (
                  <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {visitCount > 9 ? '9+' : visitCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default MobileNav;
